import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Fuse from "fuse.js";
import {
  TextField,
  Box,
  Typography,
  IconButton,
  Avatar,
  Paper,
  Chip,
  Stack,
  InputAdornment,
  Button,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import LanguageIcon from "@mui/icons-material/Language";

const EnhancedChatbox = ({ onClose }) => {
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "🤖 Welcome to GrowAITech Intelligent Assistant!\n\nI'm your AI-powered guide to our comprehensive technology solutions. I can help you with:\n\n• 🚀 Service Information & Pricing\n• 🎓 Training Programs & Internships\n• 💼 Career Opportunities\n• 📞 Contact & Support\n• 🏢 Company Information\n\nType your question or click on any topic below to get started!",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
  ]);

  const [userInput, setUserInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  
  // Quick action buttons
  const quickActions = [
    { label: "Training Programs", value: "training", icon: <SchoolIcon />, color: "#1A9E7F" },
    { label: "Internships", value: "internship", icon: <WorkIcon />, color: "#2979FF" },
    { label: "Careers", value: "careers", icon: <BusinessIcon />, color: "#FF6F00" },
    { label: "Contact Us", value: "contact", icon: <ContactPhoneIcon />, color: "#00C853" },
  ];

  // Comprehensive knowledge base
  const knowledgeBase = [
    {
      key: "web development",
      category: "Development",
      text: "🌐 **Web Development Services**\n\nAt GrowAITech, we build cutting-edge web applications using:\n\n• **Frontend**: React.js, Next.js, TypeScript, Tailwind CSS\n• **Backend**: Node.js, Python, Django, PostgreSQL\n• **DevOps**: Docker, Kubernetes, CI/CD pipelines\n• **Performance**: SEO optimization, PWA development\n\nOur solutions are scalable, secure, and optimized for performance.",
      link: "/services/web-development",
      related: ["react", "nodejs", "typescript", "full stack", "frontend", "backend"]
    },
    {
      key: "artificial intelligence",
      category: "AI & Data",
      text: "🤖 **AI & Machine Learning Solutions**\n\nTransform your business with our AI expertise:\n\n• **Custom AI Models**: NLP, Computer Vision, Predictive Analytics\n• **ML Pipelines**: Data preprocessing, model training, deployment\n• **AI Integration**: Chatbots, recommendation engines, automation\n• **Deep Learning**: TensorFlow, PyTorch, Keras\n\nWe help you leverage AI to drive innovation and efficiency.",
      link: "/services/artificial-intelligence",
      related: ["machine learning", "deep learning", "neural networks", "nlp", "computer vision"]
    },
    {
      key: "data analytics",
      category: "AI & Data",
      text: "📊 **Data Analytics & Business Intelligence**\n\nUnlock insights from your data with our comprehensive solutions:\n\n• **Data Engineering**: ETL pipelines, data warehousing\n• **Visualization**: Power BI, Tableau, D3.js dashboards\n• **Statistical Analysis**: Python, R, advanced analytics\n• **Big Data**: Hadoop, Spark, real-time processing\n\nTurn data into your competitive advantage.",
      link: "/services/data-analytics",
      related: ["data science", "business intelligence", "dashboard", "analytics", "reporting"]
    },
    {
      key: "app development",
      category: "Development",
      text: "📱 **Mobile App Development**\n\nReach customers anywhere with native and cross-platform apps:\n\n• **Native Development**: iOS (Swift), Android (Kotlin)\n• **Cross-Platform**: Flutter, React Native\n• **App Store Optimization**: ASO, deployment strategies\n• **Maintenance**: Updates, bug fixes, performance monitoring\n\nBuild engaging mobile experiences that drive growth.",
      link: "/services/app-development",
      related: ["flutter", "react native", "ios", "android", "mobile", "app"]
    },
    {
      key: "cybersecurity",
      category: "Infrastructure & Security",
      text: "🔐 **Comprehensive Cybersecurity Solutions**\n\nProtect your digital assets with our security expertise:\n\n• **Security Audits**: Vulnerability assessments, penetration testing\n• **Compliance**: GDPR, HIPAA, SOC 2 implementation\n• **Security Infrastructure**: Firewalls, IDS/IPS, SIEM\n• **Incident Response**: 24/7 monitoring, rapid response teams\n\nStay secure in an evolving threat landscape.",
      link: "/services/cybersecurity",
      related: ["network security", "ethical hacking", "vapt", "compliance", "audit"]
    },
    {
      key: "cloud computing",
      category: "Infrastructure & Security",
      text: "☁️ **Cloud Infrastructure & DevOps**\n\nModernize your infrastructure with cloud solutions:\n\n• **Cloud Migration**: AWS, Azure, GCP migration strategies\n• **Serverless Architecture**: Lambda, Functions, cost optimization\n• **DevOps Automation**: CI/CD, Infrastructure as Code\n• **Monitoring**: Performance tracking, logging, alerting\n\nScale efficiently with enterprise-grade cloud solutions.",
      link: "/services/cloud-computing",
      related: ["aws", "azure", "devops", "kubernetes", "serverless", "migration"]
    },
    {
      key: "training",
      category: "Education",
      text: "🎓 **Professional Training Programs**\n\nAdvance your career with our industry-leading training:\n\n• **Tech Skills**: Full-stack development, AI/ML, cloud computing\n• **Certification Programs**: Industry-recognized certifications\n• **Workshops**: Hands-on, project-based learning\n• **Career Support**: Resume building, interview preparation\n\nInvest in skills that drive career growth.",
      link: "/careers",
      related: ["courses", "certification", "workshop", "training", "skills"]
    },
    {
      key: "internship",
      category: "Education",
      text: "🚀 **Industry Internship Program**\n\nGain real-world experience with our internship program:\n\n• **Live Projects**: Work on actual client projects\n• **Mentorship**: Guidance from senior engineers\n• **Tech Stack**: Modern tools and frameworks\n• **Portfolio Building**: Create impressive project portfolio\n• **Placement Support**: Job placement assistance\n\nLaunch your tech career with practical experience.",
      link: "/careers",
      related: ["internship", "training", "jobs", "careers", "placement", "mentorship"]
    },
    {
      key: "careers",
      category: "Careers",
      text: "💼 **Join Our Growing Team**\n\nBe part of innovative technology solutions:\n\n• **Open Positions**: Developer, AI engineer, designer roles\n• **Company Culture**: Innovation, collaboration, growth mindset\n• **Benefits**: Competitive salary, health insurance, learning budget\n• **Work Environment**: Remote-first, flexible hours\n\nBuild the future of technology with us.",
      link: "/careers",
      related: ["jobs", "hiring", "recruitment", "company culture", "benefits"]
    },
    {
      key: "contact",
      category: "Contact",
      text: "📞 **Get in Touch**\n\nReady to start your technology journey?\n\n• **📧 Email**: growaitech@gmail.com\n• **📱 Phone**: +91 63745 49599\n• **📍 Location**: Coimbatore, Tamil Nadu, India\n• **💬 WhatsApp**: +91 63745 49599\n• **🌐 Website**: www.growaitech.com\n\nWe're here to help 24/7.",
      link: "/contact",
      related: ["email", "phone", "address", "location", "support", "whatsapp"]
    },
    {
      key: "about",
      category: "Company",
      text: "🏢 **About GrowAITech**\n\nYour trusted technology partner:\n\n• **Founded**: 2020 by industry experts\n• **Mission**: Empowering businesses with cutting-edge technology\n• **Expertise**: Web, Mobile, AI, Cloud, Security\n• **Clients**: 150+ successful projects delivered\n• **Team**: 50+ skilled professionals\n\nTransforming ideas into digital reality.",
      link: "/about",
      related: ["company", "founder", "mission", "vision", "team", "expertise"]
    },
    {
      key: "pricing",
      category: "Business",
      text: "💰 **Transparent Pricing Models**\n\nFlexible engagement options:\n\n• **Fixed Price**: For well-defined project scopes\n• **Time & Material**: For evolving requirements\n• **Dedicated Team**: Long-term partnerships\n• **Consulting**: Strategic technology advisory\n• **Support**: 24/7 maintenance and updates\n\nQuality solutions at competitive prices.",
      link: "/contact",
      related: ["cost", "pricing", "engagement models", "quotations", "investment"]
    }
  ];

  const fuse = new Fuse(knowledgeBase, {
    keys: ["key", "text", "related"],
    threshold: 0.3,
    includeScore: true,
  });

  const getBotResponse = (message) => {
    const lower = message.toLowerCase();
    
    // Direct matches first
    const exact = knowledgeBase.find(kb => lower.includes(kb.key));
    if (exact) return exact;

    // Fuzzy search
    const result = fuse.search(message);
    if (result.length > 0) {
      const bestMatch = result[0];
      return {
        text: `I found information about **${bestMatch.item.category}**:\n\n${bestMatch.item.text}`,
        link: bestMatch.item.link,
        suggestions: bestMatch.item.related.slice(0, 3)
      };
    }

    // Smart fallback responses
    if (lower.includes("hello") || lower.includes("hi")) {
      return {
        text: "Hello! I'm here to help you explore our technology solutions. What specific area interests you today?",
        suggestions: ["web development", "ai services", "training programs"]
      };
    }

    if (lower.includes("help")) {
      return {
        text: "I can assist you with:\n\n• 🛠️ Technical services and solutions\n• 📚 Training and internship programs\n• 💼 Career opportunities\n• 📞 Contact information\n• 💰 Pricing and engagement models\n\nWhat would you like to know more about?",
        suggestions: ["services", "training", "careers", "contact"]
      };
    }

    return {
      text: "I can help you with our services, training programs, or career opportunities. Try asking about:\n\n• Web development • AI solutions • Cloud computing\n• Cybersecurity • Data analytics • Mobile apps\n• Training programs • Internships • Careers\n\nOr type 'help' for more options!",
      suggestions: ["web development", "artificial intelligence", "training", "careers"]
    };
  };

  const sendMessage = (input = null) => {
    const text = input || userInput;
    if (!text.trim()) return;

    setMessages(prev => [...prev, { 
      sender: "user", 
      text, 
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
    }]);

    setIsTyping(true);
    
    setTimeout(() => {
      const botResponse = getBotResponse(text);
      setMessages(prev => [...prev, { 
        sender: "bot", 
        text: botResponse.text, 
        link: botResponse.link,
        suggestions: botResponse.suggestions,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
      }]);
      setIsTyping(false);
    }, 800 + Math.random() * 400);

    setUserInput("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") sendMessage();
  };

  const handleQuickAction = (action) => {
    sendMessage(action.value);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "white"
      }}
    >
      {/* Clean Full-Screen Header */}
      <Box 
        sx={{
          p: 2,
          background: "linear-gradient(135deg, #034522 0%, #1A9E7F 100%)",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          position: "relative"
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { 
                bgcolor: "rgba(255,255,255,0.2)",
                transform: "scale(1.05)"
              },
              transition: "all 0.2s ease"
            }}
          >
            <CloseIcon />
          </IconButton>
          <Avatar sx={{ 
            bgcolor: "white", 
            color: "#034522", 
            width: 48, 
            height: 48,
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
          }}>
            <SmartToyIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography 
              variant="h5" 
              fontWeight="700"
              fontSize="1.3rem"
            >
              GAT Assistant
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                opacity: 0.9,
                fontSize: "0.9rem"
              }}
            >
              Online • AI-Powered Support
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton 
            size="medium" 
            sx={{ 
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { 
                bgcolor: "rgba(255,255,255,0.2)",
                transform: "scale(1.05)"
              },
              transition: "all 0.2s ease"
            }}
          >
            <LanguageIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Messages Area - Maximum Coverage */}
      <Box 
        sx={{
          flex: 1,
          p: 2,
          overflowY: "auto",
          bgcolor: "#f8f9fa",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          maxHeight: "calc(100vh - 120px)"
        }}
      >
        {messages.map((msg, index) => (
          <Box 
            key={index} 
            sx={{ 
              alignSelf: msg.sender === "user" ? "flex-end" : "flex-start",
              maxWidth: "70%",
              display: "flex",
              flexDirection: "column",
              alignItems: msg.sender === "user" ? "flex-end" : "flex-start"
            }}
          >
            <Box 
              sx={{
                display: "flex",
                gap: 1.5,
                flexDirection: msg.sender === "user" ? "row-reverse" : "row",
                alignItems: "flex-start"
              }}
            >
              {msg.sender === "bot" ? (
                <Avatar sx={{ 
                  width: 40, 
                  height: 40, 
                  bgcolor: "#034522",
                  flexShrink: 0,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }}>
                  <SmartToyIcon />
                </Avatar>
              ) : (
                <Avatar sx={{ 
                  width: 40, 
                  height: 40, 
                  bgcolor: "#1976d2",
                  flexShrink: 0,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }}>
                  <PersonIcon />
                </Avatar>
              )}
              
              <Paper 
                elevation={3}
                sx={{
                  p: 2.5,
                  borderRadius: msg.sender === "user" ? "20px 20px 6px 20px" : "20px 20px 20px 6px",
                  bgcolor: msg.sender === "user" ? "#034522" : "white",
                  color: msg.sender === "user" ? "white" : "text.primary",
                  maxWidth: "100%",
                  wordBreak: "break-word",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: "pre-line", lineHeight: 1.5 }}>
                  {msg.text}
                </Typography>
                
                {msg.link && (
                  <Box mt={1.5}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      href={msg.link}
                      sx={{ 
                        fontSize: "0.75rem",
                        borderColor: msg.sender === "user" ? "rgba(255,255,255,0.3)" : "#034522",
                        color: msg.sender === "user" ? "rgba(255,255,255,0.7)" : "#034522",
                        "&:hover": {
                          bgcolor: msg.sender === "user" ? "rgba(255,255,255,0.1)" : "#03452210"
                        }
                      }}
                    >
                      Learn More →
                    </Button>
                  </Box>
                )}

                {msg.suggestions && (
                  <Box mt={1.5}>
                    <Typography variant="caption" sx={{ color: "text.secondary", mb: 1 }}>
                      Related topics:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {msg.suggestions.map((suggestion, i) => (
                        <Chip 
                          key={i}
                          label={suggestion}
                          size="small"
                          onClick={() => sendMessage(suggestion)}
                          sx={{ 
                            fontSize: "0.7rem",
                            height: 24,
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor: "#03452210"
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Paper>
            </Box>
            <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, fontSize: "0.7rem" }}>
              {msg.timestamp}
            </Typography>
          </Box>
        ))}
        
        {/* Typing Indicator */}
        {isTyping && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar sx={{ width: 24, height: 24, bgcolor: "#034522" }}>
              <SmartToyIcon fontSize="small" />
            </Avatar>
            <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
              GAT Assistant is typing...
            </Typography>
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Box>

      {/* Minimal Quick Actions */}
      <Box sx={{ 
        p: 0.5, 
        bgcolor: "white", 
        borderTop: "1px solid #e0e0e0" 
      }}>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          {quickActions.map((action) => (
            <Button
              key={action.value}
              variant="text"
              size="small"
              onClick={() => handleQuickAction(action)}
              sx={{
                color: action.color,
                fontWeight: 600,
                fontSize: "0.6rem",
                py: 0.1,
                px: 0.5,
                height: "18px",
                minWidth: "auto",
                "&:hover": {
                  bgcolor: action.color + "15"
                }
              }}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Compact Input Area */}
      <Box sx={{ 
        p: 1.5, 
        bgcolor: "white", 
        borderTop: "1px solid #e0e0e0" 
      }}>
        <TextField
          fullWidth
          placeholder="Ask about services, training, or careers..."
          variant="outlined"
          size="small"
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          onKeyPress={handleKeyPress}
          InputProps={{
            sx: { 
              borderRadius: "20px", 
              bgcolor: "#f8f9fa",
              pr: 1,
              fontSize: "0.9rem",
              py: 1
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton 
                  onClick={() => sendMessage()} 
                  disabled={!userInput.trim() || isTyping}
                  color="primary"
                  size="small"
                  sx={{ 
                    bgcolor: "#034522",
                    color: "white",
                    "&:hover": {
                      bgcolor: "#1A9E7F",
                      transform: "scale(1.05)"
                    },
                    "&:disabled": {
                      bgcolor: "#ccc"
                    },
                    transition: "all 0.2s ease"
                  }}
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

EnhancedChatbox.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default EnhancedChatbox;
